import { ensureAccessToken, hasPersistedSession } from "@/boot/auth";

export class ApiError extends Error {
  /**
   * `info`: the refusal as the server sent it, when it is an object — `{error:
   * "place_exists", message, place}` carries the place to offer instead (Spec 025).
   */
  constructor(public status: number, public detail: string, public info?: Record<string, unknown>) {
    super(detail);
  }
}

interface FetchOptions extends Omit<RequestInit, "body" | "headers"> {
  body?: unknown;
  headers?: Record<string, string>;
  /**
   * Attach an `Idempotency-Key`. `true` retains the same key while an identical
   * attempt has an ambiguous outcome. A string provides an explicit key.
   */
  idempotent?: boolean | string;
}

// Retain keys for ambiguous failures. A successful or definitively rejected
// request ends that attempt; a later deliberate create then gets a new key.
const pendingCreates = new Map<string, string>();

function requestActor(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.sub === "string" ? payload.sub : token;
  } catch {
    return token;
  }
}

export async function api<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const token = await ensureAccessToken();
  if (!token && hasPersistedSession()) throw new ApiError(503, "Не удалось восстановить соединение. Попробуй ещё раз.");
  const body = options.body === undefined ? undefined : JSON.stringify(options.body);
  const signature = JSON.stringify([requestActor(token), options.method ?? "GET", path, body]);
  const automaticKey = options.idempotent === true;
  let key = typeof options.idempotent === "string" ? options.idempotent : undefined;
  if (automaticKey) {
    key = pendingCreates.get(signature) ?? newIdempotencyKey();
    pendingCreates.set(signature, key);
  }
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...(options.idempotent
      ? {
          "idempotency-key":
            key!,
        }
      : {}),
    ...options.headers,
  };
  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
    body,
    signal: options.signal,
    credentials: "omit",
  };
  const response = await fetch(path, init);
  if (response.status === 204) {
    if (automaticKey) pendingCreates.delete(signature);
    return undefined as T;
  }
  const text = await response.text();
  const data = parseJson(text) as { detail?: unknown } | null;
  if (automaticKey && response.status >= 400 && response.status < 500 && response.status !== 408) {
    pendingCreates.delete(signature);
  }
  if (!response.ok) {
    const conflict = asConflict(response.status, data?.detail);
    if (conflict) throw conflict;
    // A FastAPI 422 hands back a list of field errors, not a string; printing
    // "[object Object]" at a person is worse than printing nothing useful.
    const detail = data?.detail;
    throw new ApiError(
      response.status,
      typeof detail === "string" ? detail : detail && typeof detail === "object" && "message" in detail && typeof detail.message === "string" ? detail.message : response.statusText || "Не удалось сохранить. Попробуй ещё раз.",
      detail && typeof detail === "object" && !Array.isArray(detail) ? (detail as Record<string, unknown>) : undefined,
    );
  }
  if (text && data === null) throw new ApiError(response.status, "Сервер вернул непонятный ответ. Повтори запрос.");
  if (automaticKey) pendingCreates.delete(signature);
  return data as T;
}

/** A non-JSON body (an HTML error page from a proxy) must not throw here. */
function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0–100, or null while the browser has not reported a computable length. */
  percent: number | null;
}

/**
 * Multipart POST with real upload progress.
 *
 * `fetch` cannot report how much of a request body has gone out, and the design
 * spec asks the upload button to show progress rather than an indeterminate
 * spinner — so this one call drops to XMLHttpRequest. Everything else in the
 * app goes through `api()`.
 *
 * The body is a FormData and the content-type header is deliberately NOT set:
 * the browser has to add its own `boundary=…`, and naming the type by hand
 * strips it and makes the server reject the parts.
 */
export async function upload<T>(
  path: string,
  form: FormData,
  onProgress?: (progress: UploadProgress) => void,
  idempotencyKey?: string,
): Promise<T> {
  const token = await ensureAccessToken();
  if (!token && hasPersistedSession()) throw new ApiError(503, "Не удалось восстановить соединение. Попробуй ещё раз.");
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", path);
    if (token) xhr.setRequestHeader("authorization", `Bearer ${token}`);
    if (idempotencyKey) xhr.setRequestHeader("Idempotency-Key", idempotencyKey);

    xhr.upload.onprogress = (e) => {
      onProgress?.({
        loaded: e.loaded,
        total: e.total,
        percent: e.lengthComputable ? Math.round((e.loaded / e.total) * 100) : null,
      });
    };
    xhr.onerror = () => reject(new ApiError(0, "Сеть недоступна"));
    xhr.onabort = () => reject(new ApiError(0, "Загрузка отменена"));
    xhr.onload = () => {
      const data = parseJson(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data as T);
        return;
      }
      const detail =
        (data as { detail?: string } | null)?.detail ?? xhr.statusText ?? "Ошибка загрузки";
      reject(new ApiError(xhr.status, detail));
    };
    xhr.send(form);
  });
}

/**
 * GET a binary body with the bearer token attached.
 *
 * Attachment downloads cannot be a plain `<a href>`: the API authenticates by
 * `Authorization` header only (no cookie, no query token), so a bare link
 * would 401. We fetch the bytes, hand the browser an object URL, and revoke it
 * — which also means the server's `content-disposition` is honoured for the
 * filename we pass through.
 */
export async function fetchBlob(path: string): Promise<Blob> {
  const token = await ensureAccessToken();
  const response = await fetch(path, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: "omit",
  });
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText || "Не удалось скачать файл");
  }
  return response.blob();
}

/**
 * A 409 from the optimistic lock (SPEC-METHODS §5.3).
 *
 * Kept as its own error class rather than a status check, because the UI has
 * to do something specific with it: show what changed and let the person
 * choose. A blind retry is not reachable from the interface at all — the two
 * ways to get it wrong are re-sending the same stale `version` (which fails
 * again) and dropping `version` entirely (which overwrites someone's work
 * silently), and neither is a button.
 */
export class VersionConflictError extends ApiError {
  constructor(
    public expected: number,
    public actual: number,
    public current: Record<string, unknown>,
  ) {
    super(409, "Запись изменилась, пока ты смотрел");
  }
}

interface ConflictDetail {
  error?: string;
  expected?: number;
  actual?: number;
  current?: Record<string, unknown>;
}

function asConflict(status: number, detail: unknown): VersionConflictError | null {
  if (status !== 409 || typeof detail !== "object" || detail === null) return null;
  const body = detail as ConflictDetail;
  if (body.error !== "version_conflict") return null;
  return new VersionConflictError(body.expected ?? 0, body.actual ?? 0, body.current ?? {});
}

/**
 * A fresh key per creating call (SPEC-METHODS §5.3). Two deliberate creates
 * must not collapse into one, so this is never reused across calls — a retry
 * that wants the first attempt's entity passes the key it already used.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
